import React from "react";
import { act, render, screen } from "@testing-library/react";
import "@testing-library/jest-dom";

import {
  AgentGemstone,
  getAgentGemstoneBubbleTone,
  getGemstoneMeshDiagnostics,
  getGemstoneMeshTopology,
} from "./AgentGemstone";
import { AGENT_AVATAR_COLOURS, AGENT_AVATAR_CUTS } from "./agentAvatarIdentity";

describe("AgentGemstone", () => {
  it("derives a light, legible reply tint from every resolved palette colour", () => {
    for (const colour of AGENT_AVATAR_COLOURS) {
      const tone = getAgentGemstoneBubbleTone(77, {
        cut: "torus",
        colour,
      });
      expect(tone.colour).toBe(colour);
      expect(tone.backgroundColor).toMatch(/^hsl\(\d+ \d+% 94%\)$/);
      expect(tone.color).toMatch(/^hsl\(\d+ \d+% 20%\)$/);
    }
  });

  it("keeps every supported cut visibly faceted at changing angles", () => {
    for (const cut of AGENT_AVATAR_CUTS) {
      const initialView = getGemstoneMeshDiagnostics(cut, 0);
      const turnedView = getGemstoneMeshDiagnostics(cut, Math.PI / 3);
      expect(initialView.totalFaceCount).toBeGreaterThan(0);
      expect(initialView.visibleFaceIndexes.length).toBeGreaterThan(0);
      expect(turnedView.visibleFaceIndexes.length).toBeGreaterThan(0);
      expect(turnedView.projectedVertices).not.toEqual(
        initialView.projectedVertices,
      );
    }
  });

  it("pins cap/fan and torus inner/outer normals with cut-aware winding", () => {
    const normalFor = (vertices: number[][], face: number[]) => {
      const [a, b, c] = face.map((index) => vertices[index]);
      const ux = b[0] - a[0];
      const uy = b[1] - a[1];
      const uz = b[2] - a[2];
      const vx = c[0] - a[0];
      const vy = c[1] - a[1];
      const vz = c[2] - a[2];
      return [uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx];
    };
    for (const cut of ["rhomboid", "tablet", "column"] as const) {
      const { vertices, faces } = getGemstoneMeshTopology(cut);
      const floor = Math.min(...vertices.map((vertex) => vertex[1]));
      const ceiling = Math.max(...vertices.map((vertex) => vertex[1]));
      const lowerCap = faces.find((face) =>
        face.every((index) => vertices[index][1] === floor),
      );
      const upperCap = faces.find((face) =>
        face.every((index) => vertices[index][1] === ceiling),
      );
      expect(lowerCap).toBeDefined();
      expect(upperCap).toBeDefined();
      expect(normalFor(vertices, lowerCap!)[1]).toBeLessThan(0);
      expect(normalFor(vertices, upperCap!)[1]).toBeGreaterThan(0);
    }
    for (const cut of ["sphere", "bipyramid"] as const) {
      const { vertices, faces } = getGemstoneMeshTopology(cut);
      const lowerApex = vertices.reduce(
        (lowest, vertex, index) =>
          vertex[1] < vertices[lowest][1] ? index : lowest,
        0,
      );
      const upperApex = vertices.reduce(
        (highest, vertex, index) =>
          vertex[1] > vertices[highest][1] ? index : highest,
        0,
      );
      expect(
        normalFor(
          vertices,
          faces.find((face) => face.includes(lowerApex))!,
        )[1],
      ).toBeLessThan(0);
      expect(
        normalFor(
          vertices,
          faces.find((face) => face.includes(upperApex))!,
        )[1],
      ).toBeGreaterThan(0);
    }
    const { vertices, faces } = getGemstoneMeshTopology("torus");
    for (const face of faces) {
      const normal = normalFor(vertices, face);
      const center = face
        .reduce(
          (sum, index) => [
            sum[0] + vertices[index][0],
            sum[1] + vertices[index][1],
            sum[2] + vertices[index][2],
          ],
          [0, 0, 0],
        )
        .map((value) => value / face.length);
      const length = Math.hypot(center[0], center[2]) || 1;
      const ringCenter = [center[0] / length, 0, center[2] / length];
      const outward = [
        center[0] - ringCenter[0],
        center[1],
        center[2] - ringCenter[2],
      ];
      expect(
        normal[0] * outward[0] +
          normal[1] * outward[1] +
          normal[2] * outward[2],
      ).toBeGreaterThan(0);
    }
  });

  it("connects each torus facet around the tube before advancing around the ring", () => {
    const { faces } = getGemstoneMeshTopology("torus");
    expect(faces).toHaveLength(84);

    faces.forEach(([first, second, third, fourth]) => {
      const ringIndex = Math.floor(first / 7);
      const tubeIndex = first % 7;
      const nextRingIndex = (ringIndex + 1) % 12;
      const nextTubeIndex = (tubeIndex + 1) % 7;

      expect([second, third, fourth]).toEqual([
        ringIndex * 7 + nextTubeIndex,
        nextRingIndex * 7 + nextTubeIndex,
        nextRingIndex * 7 + tubeIndex,
      ]);
    });
  });

  it("keeps a faceted open torus at inventory, chat, and picker sizes through a full rotation", () => {
    for (const rotation of Array.from(
      { length: 12 },
      (_, index) => (index * Math.PI * 2) / 12,
    )) {
      const view = getGemstoneMeshDiagnostics("torus", rotation);
      expect(view.sortFaces).toBe(true);
      expect(view.visibleFaceIndexes.length).toBeGreaterThan(0);
      expect(view.visibleFaceIndexes.length).toBeLessThan(view.totalFaceCount);
      view.visibleFaceDepths.slice(1).forEach((depth, index) => {
        expect(depth).toBeGreaterThanOrEqual(view.visibleFaceDepths[index]);
      });
    }

    render(
      <>
        <AgentGemstone
          id={1}
          cut="torus"
          colour="azure"
          size={16}
          state="error"
          testId="torus-strip"
        />
        <AgentGemstone
          id={2}
          cut="torus"
          colour="azure"
          size={32}
          state="error"
          testId="torus-chat"
        />
        <AgentGemstone
          id={3}
          cut="torus"
          colour="azure"
          size={38}
          state="error"
          testId="torus-inventory"
        />
        <AgentGemstone
          id={4}
          cut="torus"
          colour="azure"
          size={72}
          state="error"
          testId="torus-picker"
        />
      </>,
    );

    for (const [testId, size] of [
      ["torus-strip", 16],
      ["torus-chat", 32],
      ["torus-inventory", 38],
      ["torus-picker", 72],
    ] as const) {
      const gemstone = screen.getByTestId(testId);
      expect(gemstone).toHaveAttribute("width", String(size));
      expect(gemstone).toHaveAttribute("height", String(size));
      expect(screen.getByTestId(`${testId}-face-0`)).toBeInTheDocument();
    }
  });

  it("uses a changing polygon projection when motion is allowed", () => {
    const matchMedia = window.matchMedia;
    const requestAnimationFrame = window.requestAnimationFrame;
    const cancelAnimationFrame = window.cancelAnimationFrame;
    const performanceNow = jest.spyOn(performance, "now").mockReturnValue(0);
    const callbacks: FrameRequestCallback[] = [];
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: jest.fn(() => ({ matches: false })),
    });
    Object.defineProperty(window, "requestAnimationFrame", {
      configurable: true,
      value: jest.fn((callback: FrameRequestCallback) => {
        callbacks.push(callback);
        return callbacks.length;
      }),
    });
    Object.defineProperty(window, "cancelAnimationFrame", {
      configurable: true,
      value: jest.fn(),
    });

    const { unmount } = render(
      <AgentGemstone
        id={17}
        cut="brilliant"
        colour="azure"
        testId="animated-gemstone"
      />,
    );
    const gemstone = screen.getByTestId("animated-gemstone");
    const beforeRotation = gemstone.getAttribute("data-gemstone-rotation");
    const beforePoints = screen
      .getByTestId("animated-gemstone-face-0")
      .getAttribute("points");
    expect(callbacks).toHaveLength(1);

    act(() => callbacks.shift()?.(50));

    expect(gemstone.getAttribute("data-gemstone-rotation")).not.toBe(
      beforeRotation,
    );
    expect(
      screen.getByTestId("animated-gemstone-face-0").getAttribute("points"),
    ).not.toBe(beforePoints);
    unmount();
    expect(window.cancelAnimationFrame).toHaveBeenCalled();
    performanceNow.mockRestore();
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: matchMedia,
    });
    Object.defineProperty(window, "requestAnimationFrame", {
      configurable: true,
      value: requestAnimationFrame,
    });
    Object.defineProperty(window, "cancelAnimationFrame", {
      configurable: true,
      value: cancelAnimationFrame,
    });
  });

  it("renders missing appearances as a neutral unassigned placeholder", () => {
    const matchMedia = window.matchMedia;
    const requestAnimationFrame = window.requestAnimationFrame;
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: jest.fn(() => ({ matches: true })),
    });
    Object.defineProperty(window, "requestAnimationFrame", {
      configurable: true,
      value: jest.fn(),
    });

    const { rerender } = render(
      <AgentGemstone id={77} testId="unassigned-gemstone" />,
    );
    const placeholder = screen.getByTestId("unassigned-gemstone");
    expect(placeholder).toHaveAttribute("data-gemstone-status", "unassigned");
    expect(placeholder).not.toHaveAttribute("data-gemstone-cut");
    expect(placeholder).not.toHaveAttribute("data-gemstone-colour");
    expect(
      screen.getByTestId("unassigned-gemstone-unassigned-placeholder"),
    ).toHaveAttribute("fill", "#f1f3f5");
    expect(placeholder).toHaveAccessibleName("Agent appearance unassigned");
    expect(window.requestAnimationFrame).not.toHaveBeenCalled();
    rerender(<AgentGemstone id={170} testId="unassigned-gemstone" />);
    expect(placeholder.getAttribute("data-gemstone-rotation")).toBe("0.0000");
    expect(placeholder).not.toHaveAttribute("data-gemstone-colour");

    const { unmount: unmountOtherAgent } = render(
      <AgentGemstone id={42} testId="other-unassigned-gemstone" />,
    );
    const otherPlaceholder = screen.getByTestId("other-unassigned-gemstone");
    expect(otherPlaceholder).toHaveAttribute(
      "data-gemstone-status",
      "unassigned",
    );
    expect(otherPlaceholder).not.toHaveAttribute("data-gemstone-cut");
    expect(otherPlaceholder).not.toHaveAttribute("data-gemstone-colour");
    unmountOtherAgent();

    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: matchMedia,
    });
    Object.defineProperty(window, "requestAnimationFrame", {
      configurable: true,
      value: requestAnimationFrame,
    });
  });

  it("uses accessible, static status circles when reduced motion disables animation", () => {
    const matchMedia = window.matchMedia;
    const requestAnimationFrame = window.requestAnimationFrame;
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: jest.fn(() => ({ matches: true })),
    });
    Object.defineProperty(window, "requestAnimationFrame", {
      configurable: true,
      value: jest.fn(),
    });
    const stoppedAt = new Date(Date.now() - 1).toISOString();
    render(
      <>
        <AgentGemstone
          id={1}
          cut="brilliant"
          colour="azure"
          agentStatus="generating"
          title="Working agent"
          showStatusCircle
          testId="working-gemstone"
        />
        <AgentGemstone
          id={2}
          cut="torus"
          colour="rose"
          agentStatus="finished"
          agentLastHeartbeatAt={stoppedAt}
          title="Finished agent"
          showStatusCircle
          testId="finished-gemstone"
        />
        <AgentGemstone
          id={3}
          cut="sphere"
          colour="green"
          agentStatus="idle"
          title="Idle agent"
          showStatusCircle
          testId="idle-gemstone"
        />
      </>,
    );
    expect(screen.getByTestId("working-gemstone")).toHaveAttribute(
      "data-gemstone-status-circle",
      "working",
    );
    expect(screen.getByTestId("finished-gemstone")).toHaveAttribute(
      "data-gemstone-status-circle",
      "finished",
    );
    expect(screen.getByTestId("idle-gemstone")).toHaveAttribute(
      "data-gemstone-status-circle",
      "none",
    );
    expect(screen.getByTestId("working-gemstone")).toHaveAttribute(
      "data-gemstone-motion",
      "reduced",
    );
    expect(
      screen.getByTestId("working-gemstone-status-circle"),
    ).toHaveAttribute("fill", "#2e7d32");
    expect(
      screen.getByTestId("finished-gemstone-status-circle"),
    ).toHaveAttribute("fill", "#d6a600");
    expect(
      screen.queryByText(/^(working|finished|idle)$/i),
    ).not.toBeInTheDocument();
    expect(
      screen.getByLabelText("Working agent; working status"),
    ).toBeInTheDocument();
    expect(screen.getByTestId("working-gemstone")).toHaveStyle({
      transform: "scale(1)",
      transition: "none",
    });
    expect(window.requestAnimationFrame).not.toHaveBeenCalled();
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: matchMedia,
    });
    Object.defineProperty(window, "requestAnimationFrame", {
      configurable: true,
      value: requestAnimationFrame,
    });
  });

  it("keeps status decoration opt-in so attributed message gemstones remain identity-only", () => {
    const stoppedAt = new Date(Date.now() - 1).toISOString();
    render(
      <>
        <AgentGemstone
          id={1}
          agentStatus="finished"
          agentLastHeartbeatAt={stoppedAt}
          testId="header-gemstone"
          showStatusCircle
        />
        <AgentGemstone
          id={1}
          agentStatus="finished"
          agentLastHeartbeatAt={stoppedAt}
          testId="message-gemstone"
        />
      </>,
    );
    expect(screen.getByTestId("header-gemstone")).toHaveAttribute(
      "data-gemstone-status-circle",
      "finished",
    );
    expect(screen.getByTestId("message-gemstone")).toHaveAttribute(
      "data-gemstone-status-circle",
      "none",
    );
    expect(
      screen.queryByTestId("message-gemstone-status-circle"),
    ).not.toBeInTheDocument();
  });

  it("gives working status a faster bounded presentation without changing SVG dimensions", () => {
    const matchMedia = window.matchMedia;
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: jest.fn(() => ({ matches: false })),
    });

    render(
      <AgentGemstone
        id={9}
        agentStatus="generating"
        size={38}
        testId="working-gemstone"
        showStatusCircle
      />,
    );

    const gemstone = screen.getByTestId("working-gemstone");
    expect(gemstone).toHaveAttribute("width", "38");
    expect(gemstone).toHaveAttribute("height", "38");
    expect(gemstone).toHaveStyle({
      overflow: "visible",
      transform: "scale(1.055)",
    });
    expect(
      screen.getByTestId("working-gemstone-status-circle"),
    ).toHaveAttribute("data-gemstone-layer", "background");
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: matchMedia,
    });
  });

  it("rotates working gemstones faster than the prior 1.8 radians-per-second rate", () => {
    const matchMedia = window.matchMedia;
    const requestAnimationFrame = window.requestAnimationFrame;
    const cancelAnimationFrame = window.cancelAnimationFrame;
    const performanceNow = jest.spyOn(performance, "now").mockReturnValue(0);
    const callbacks: FrameRequestCallback[] = [];
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: jest.fn(() => ({ matches: false })),
    });
    Object.defineProperty(window, "requestAnimationFrame", {
      configurable: true,
      value: jest.fn((callback: FrameRequestCallback) => {
        callbacks.push(callback);
        return callbacks.length;
      }),
    });
    Object.defineProperty(window, "cancelAnimationFrame", {
      configurable: true,
      value: jest.fn(),
    });

    render(
      <>
        <AgentGemstone
          id={41}
          agentStatus="generating"
          testId="working-gemstone"
          showStatusCircle
        />
        <AgentGemstone
          id={42}
          agentStatus="idle"
          testId="idle-gemstone"
          showStatusCircle
        />
      </>,
    );
    const initialWorking = Number(
      screen
        .getByTestId("working-gemstone")
        .getAttribute("data-gemstone-rotation"),
    );
    const initialIdle = Number(
      screen
        .getByTestId("idle-gemstone")
        .getAttribute("data-gemstone-rotation"),
    );
    expect(callbacks).toHaveLength(2);

    act(() => {
      callbacks[0](50);
      callbacks[1](50);
    });

    const workingDelta =
      Number(
        screen
          .getByTestId("working-gemstone")
          .getAttribute("data-gemstone-rotation"),
      ) - initialWorking;
    const idleDelta =
      Number(
        screen
          .getByTestId("idle-gemstone")
          .getAttribute("data-gemstone-rotation"),
      ) - initialIdle;
    expect(workingDelta).toBeCloseTo(0.12, 3);
    expect(workingDelta).toBeGreaterThan(0.09);
    expect(workingDelta).toBeGreaterThan(idleDelta * 4);

    performanceNow.mockRestore();
    Object.defineProperty(window, "matchMedia", {
      configurable: true,
      value: matchMedia,
    });
    Object.defineProperty(window, "requestAnimationFrame", {
      configurable: true,
      value: requestAnimationFrame,
    });
    Object.defineProperty(window, "cancelAnimationFrame", {
      configurable: true,
      value: cancelAnimationFrame,
    });
  });
});
